import * as React from 'react';
import { Icons } from './icons';
import { SemanticIcons, type SemanticIconType } from './SemanticIcons';
import { StartupIcon } from './StartupIcon';

export type IconType = keyof typeof Icons;
export type EnhancedIconType = IconType | SemanticIconType;

type BaseProps = {
  size?: number | string;
  className?: string;
  color?: string;
  variant?: 'default' | 'monochrome' | 'outline' | 'filled';
};

type LegacyProps = {
  name: IconType;
} & React.SVGProps<SVGSVGElement> &
  BaseProps;

type SemanticProps = {
  name: SemanticIconType;
} & BaseProps;

type Props = LegacyProps | SemanticProps;

/**
 * Enhanced Icon component that supports both legacy SVG icons and new semantic icons.
 * Uses the startup logo as the base for all semantic icons to ensure consistent branding.
 *
 * @param name - The name of the icon to render
 * @param size - Size of the icon (number in px or string)
 * @param className - Additional CSS classes
 * @param color - Color override for the icon
 * @param variant - Visual variant of the icon (default, monochrome, outline, filled)
 */
export const Icon = ({
  name,
  size = 24,
  className = '',
  color,
  variant = 'default',
  ...rest
}: Props) => {
  // Check if it's a semantic icon
  if (name in SemanticIcons) {
    const SemanticIconComponent = SemanticIcons[name as SemanticIconType];
    return <SemanticIconComponent size={size} className={className} color={color} {...rest} />;
  }

  // Fallback to legacy SVG icons
  const IconComponent = Icons[name as IconType];
  if (!IconComponent) {
    console.warn(`Icon "${name}" not found. Using startup logo as fallback.`);
    return (
      <StartupIcon size={size} className={className} color={color} variant={variant} {...rest} />
    );
  }

  return <IconComponent {...rest} />;
};

/**
 * Direct access to the startup logo icon for consistent branding
 */
export { StartupIcon };

/**
 * Direct access to semantic icons for advanced usage
 */
export { SemanticIcons };
